import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { OPERATOR_LABEL } from '@/features/notifications/customRuleExpression';

/**
 * Manual dos alarmes personalizados, para administrador e revenda.
 *
 * É texto, não captura de tela, e isso é deliberado: as telas do NautMax ainda vão mudar nas
 * próximas iterações da feature, e manual com imagem desatualizada engana mais do que ajuda.
 * Quando a tela estabilizar, dá para acrescentar as capturas aqui.
 */

const OPERADORES: { op: keyof typeof OPERATOR_LABEL; quando: string; exemplo: string }[] = [
  { op: 'eq', quando: 'o valor é exatamente um', exemplo: 'input igual a 101' },
  { op: 'ne', quando: 'qualquer valor menos um', exemplo: 'ignition diferente de true' },
  { op: 'gt', quando: 'passou de um limite', exemplo: 'coolantTemp maior que 95' },
  { op: 'lt', quando: 'caiu abaixo de um limite', exemplo: 'battery menor que 11' },
  { op: 'gte', quando: 'limite incluindo o valor', exemplo: 'rpm maior ou igual a 3000' },
  { op: 'lte', quando: 'limite incluindo o valor', exemplo: 'fuel menor ou igual a 10' },
  { op: 'in', quando: 'vale qualquer um de vários', exemplo: 'input na lista 100, 101' },
  { op: 'between', quando: 'dentro de uma faixa', exemplo: 'rpm entre 1000 e 2000' },
  {
    op: 'digitOn',
    quando: 'uma entrada específica, ignorando as outras',
    exemplo: 'input com a entrada da casa 3 ligada',
  },
];

export function CustomAlarmsManual() {
  return (
    <div className="space-y-5 p-4 sm:p-6">
      <Link
        to="/manuais"
        className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5"
      >
        <ChevronLeft className="h-5 w-5" />
        Voltar para os manuais
      </Link>

      <header className="max-w-3xl space-y-2">
        <p className="text-[11px] font-bold uppercase tracking-widest text-primary">
          Manual do administrador e da revenda
        </p>
        <h1 className="text-2xl font-bold">Alarmes personalizados</h1>
        <p className="text-sm text-muted-foreground">
          Como transformar qualquer informação que o rastreador envia, uma entrada digital, um sensor, uma
          leitura do motor, em um alarme com nome próprio, que chega no aplicativo e no e-mail de quem
          precisa saber.
        </p>
      </header>

      <Card className="max-w-3xl">
        <CardContent className="space-y-3 p-5 text-sm">
          <h2 className="text-base font-bold">Por que essa tela existe</h2>
          <p>
            A plataforma já entende uma lista de alarmes de fábrica: SOS, corte de energia, excesso de
            velocidade, água no porão em quem reporta isso de forma padronizada. Tudo o que está fora dessa
            lista chega do equipamento, fica guardado na posição da embarcação e para por ali. Ninguém é
            avisado, porque o sistema não sabe que aquilo é um alarme.
          </p>
          <p>
            Um alarme personalizado resolve exatamente isso. Você diz qual informação observar, qual valor
            significa problema e que nome esse alarme deve ter. A partir daí ele passa a existir como qualquer
            outro: aparece no histórico, entra em relatório e dispara aviso.
          </p>
          <p className="rounded-md bg-secondary/60 px-3 py-2 text-xs text-muted-foreground">
            Exemplo que vale a pena ter em toda embarcação: a boia do porão ligada em uma entrada do
            rastreador. Quando a água sobe, a boia fecha o contato, a entrada muda de valor e o alarme chega no
            celular em poucos segundos.
          </p>
        </CardContent>
      </Card>

      <Card className="max-w-3xl">
        <CardContent className="space-y-3 p-5 text-sm">
          <h2 className="text-base font-bold">Como criar, passo a passo</h2>
          <ol className="list-decimal space-y-2 pl-5">
            <li>
              <b>Dê um nome ao alarme.</b> Curto, em maiúsculas, sem vírgula e sem aspa simples. É esse texto
              que vai aparecer no histórico e no aviso.
            </li>
            <li>
              <b>Descubra o que a embarcação manda.</b> Em "Descobrir atributos do veículo", escolha a
              embarcação e clique em ler posições. A tela lista o que aquele equipamento realmente envia, com o
              valor atual de cada campo. Use esses nomes: um campo que aquele rastreador não manda faz a
              condição nunca disparar.
            </li>
            <li>
              <b>Monte a condição.</b> Escolha o campo, o operador e o valor. Pode acrescentar mais de uma
              condição e combinar com E, quando todas precisam valer ao mesmo tempo, ou com OU, quando basta
              uma delas.
            </li>
            <li>
              <b>Teste antes de salvar.</b> Escolha uma embarcação de referência e clique em testar. A resposta
              diz se a condição está satisfeita neste momento. Não estar satisfeita é o resultado esperado
              quando o gatilho não está ativo agora; o que importa é não haver erro de sintaxe.
            </li>
            <li>
              <b>Escolha canais, alcance e destinatários.</b> O alcance é obrigatório: a condição só é avaliada
              nas embarcações vinculadas.
            </li>
            <li>
              <b>Salve.</b> São criadas duas metades ligadas pelo nome: a condição, que fica nesta tela, e o
              envio, que aparece junto das demais regras em Notificações.
            </li>
          </ol>
        </CardContent>
      </Card>

      <Card className="max-w-3xl">
        <CardContent className="space-y-3 p-5 text-sm">
          <h2 className="text-base font-bold">O nome do alarme não é um rótulo</h2>
          <p>
            Ele é a peça que liga as duas metades: a condição grava esse texto na embarcação, e o envio procura
            exatamente esse texto para saber a quem avisar. Se os dois lados deixarem de bater, o alarme
            continua sendo gravado e ninguém mais é notificado.
          </p>
          <p>
            Na prática isso significa três cuidados. O nome não pode ter vírgula, porque a vírgula separa
            alarmes na hora de filtrar. Evite reaproveitar o nome de um alarme de fábrica, senão regras antigas
            passam a disparar junto com a sua. E se precisar renomear depois, renomeie por esta tela, que
            ajusta as duas metades de uma vez.
          </p>
        </CardContent>
      </Card>

      <Card className="max-w-3xl border-status-ready/40">
        <CardContent className="space-y-3 p-5 text-sm">
          <h2 className="flex items-center gap-2 text-base font-bold">
            <AlertTriangle className="h-4 w-4 text-status-ready" />
            Três avisos que a tela mostra
          </h2>
          <p className="text-muted-foreground">
            Não são obstáculos burocráticos. Cada um corresponde a uma forma de a notificação falhar em
            silêncio, sem erro na tela e sem aviso nenhum para quem cadastrou.
          </p>
          <div>
            <h3 className="font-semibold">Canal ligado que não vai entregar</h3>
            <p className="text-muted-foreground">
              O aplicativo só recebe se a pessoa já tiver entrado nele e autorizado notificações no aparelho.
              Faltando isso, a mensagem não sai e a regra continua parecendo perfeita. A tela confere
              destinatário por destinatário e diz o nome de quem não vai receber.
            </p>
          </div>
          <div>
            <h3 className="font-semibold">Outras pessoas também podem receber</h3>
            <p className="text-muted-foreground">
              Quem tem uma regra de alarme cadastrada sem escolher subtipo recebe qualquer alarme daquela
              embarcação, inclusive o seu personalizado. Vale conferir antes se a embarcação já tem uma regra
              assim.
            </p>
          </div>
          <div>
            <h3 className="font-semibold">Enquanto a condição valer, ela substitui os outros alarmes</h3>
            <p className="text-muted-foreground">
              O alarme personalizado ocupa o mesmo campo que o rastreador usa para reportar corte de energia e
              SOS. Se um deles chegar no exato momento em que sua condição está ativa, o seu prevalece. Prefira
              condições que representem um estado curto, e não algo que fique ligado o dia inteiro.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-3xl">
        <CardContent className="space-y-3 p-5 text-sm">
          <h2 className="text-base font-bold">Quando repete e quando para</h2>
          <p>
            O alarme dispara uma vez por acionamento. Enquanto a condição continuar verdadeira não há
            repetição, mesmo que a embarcação mande dezenas de posições. Assim que ela deixa de valer, a regra
            rearma sozinha e o próximo acionamento avisa de novo. É por isso que segurar a boia levantada por
            cinco minutos gera um aviso, e não duzentos.
          </p>
        </CardContent>
      </Card>

      <Card className="max-w-3xl">
        <CardContent className="space-y-3 p-5 text-sm">
          <h2 className="text-base font-bold">Os operadores disponíveis</h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[32rem] text-left text-xs">
              <thead className="border-b border-border">
                <tr>
                  <th className="py-1.5 pr-3 font-semibold">Operador</th>
                  <th className="py-1.5 pr-3 font-semibold">Quando usar</th>
                  <th className="py-1.5 font-semibold">Exemplo</th>
                </tr>
              </thead>
              <tbody>
                {OPERADORES.map((o) => (
                  <tr key={String(o.op)} className="border-b border-border/60">
                    <td className="py-1.5 pr-3 font-medium">{OPERATOR_LABEL[o.op]}</td>
                    <td className="py-1.5 pr-3 text-muted-foreground">{o.quando}</td>
                    <td className="py-1.5 font-mono text-[11px]">{o.exemplo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">
            A coluna de exemplo usa o caso do porão, em que <code className="rounded bg-secondary px-1">input</code> é o
            campo que reúne o estado das entradas do rastreador. O nome do campo varia conforme o equipamento,
            por isso a descoberta pela embarcação é o caminho seguro.
          </p>

          <h3 className="pt-1 font-semibold">Por que existe um operador só para entrada</h3>
          <p className="text-xs text-muted-foreground">
            A maioria dos rastreadores não manda uma informação por entrada. Manda todas juntas num
            número só, em que cada casa representa uma. Um Suntech com a ignição ligada e a entrada 2
            acionada envia <code className="rounded bg-secondary px-1">00000101</code>, que chega aqui como
            o número <code className="rounded bg-secondary px-1">101</code>: a casa da direita é a ignição
            e a das centenas é a entrada 2.
          </p>
          <p className="text-xs text-muted-foreground">
            Isso cria uma armadilha. Uma regra escrita como <em>input igual a 101</em> funciona no teste e
            para de funcionar no dia em que outra entrada for acionada ao mesmo tempo, porque o número
            inteiro muda e a igualdade deixa de valer. E não avisa: o alarme simplesmente nunca mais
            dispara. O operador de entrada pergunta só pela casa que interessa e ignora o resto do
            número, então continua certo com qualquer combinação das outras entradas.
          </p>
          <p className="text-xs text-muted-foreground">
            Conte as casas da direita para a esquerda, começando em 1. Qual casa corresponde a qual
            entrada depende do equipamento, então acione a entrada no aparelho e veja o número mudar
            na descoberta pela embarcação: a casa que mudou é a sua.
          </p>
        </CardContent>
      </Card>

      <Card className="max-w-3xl">
        <CardContent className="space-y-3 p-5 text-sm">
          <h2 className="text-base font-bold">Se algo não chegar</h2>
          <p>
            O botão Diagnóstico, no topo da tela de alarmes personalizados, mostra a cadeia inteira para uma
            embarcação: se a posição chegou e com quais atributos, se a condição está satisfeita agora, quais
            eventos nasceram nas últimas duas horas e quais regras casam com cada um deles.
          </p>
          <p>
            Há um quinto elo que nenhuma tela consegue mostrar: a entrega em si. Quando um canal falha nesse
            ponto, o servidor grava um aviso no log e segue adiante, sem devolver erro. Por isso o diagnóstico
            termina apontando para o cadastro do destinatário, que é onde esse tipo de falha costuma estar.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
